import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, Plus, Trash2 } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://thebetterfare-backend-production.up.railway.app'}/api`;
const VISA_IMAGE = "/images/visa.png";
const WHATSAPP_GROUP = "https://chat.whatsapp.com/FTpF0TyZG2nJkWpWKPdlrM?mode=hqrc";
const TURNSTILE_SITE_KEY = "0x4AAAAAAC8k_BC0s3zVYBE5";

const COUNTRY_CODES = {
  "United Kingdom": "gb",
  "United States": "us",
  "Canada": "ca",
  "United Arab Emirates": "ae",
  "Qatar": "qa",
  "Saudi Arabia": "sa",
  "Pakistan": "pk",
  "Malaysia": "my",
  "Singapore": "sg",
  "Vietnam": "vn",
  "Azerbaijan": "az",
  "Bahrain": "bh",
  "Oman": "om",
  "Kuwait": "kw",
  "Turkey": "tr",
  "Thailand": "th",
  "Indonesia": "id",
  "China": "cn",
  "India": "in",
  "Schengen (EU)": "eu",
};

function FlagImage({ country }) {
  const code = COUNTRY_CODES[country];
  if (!code) return null;
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      alt={country}
      style={{ width: 22, height: 15, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
    />
  );
}

const VISA_COUNTRIES = [
  "United Kingdom",
  "United States",
  "Canada",
  "United Arab Emirates",
  "Qatar",
  "Saudi Arabia",
  "Pakistan",
  "Malaysia",
  "Singapore",
  "Vietnam",
  "Azerbaijan",
  "Bahrain",
  "Oman",
  "Kuwait",
  "Turkey",
  "Thailand",
  "Indonesia",
  "China",
  "India",
  "Schengen (EU)",
];

const VISA_TYPES = [
  "Tourist / Visit",
  "Business",
  "Family Visit",
  "Transit",
  "Study",
  "Work",
  "Other",
];

const EMPTY_TRAVELLER = { full_name: "", nationality: "", date_of_birth: "", passport_number: "" };

export default function VisaRequestPage() {
  useEffect(() => { document.title = "Visa Services | The Better Fare"; }, []);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestNumber, setRequestNumber] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    destination_country: "",
    visa_type: "",
    nationality: "",
    travel_date: "",
    travellers: [{ ...EMPTY_TRAVELLER }],
    customer_notes: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addTraveller = () => {
    setFormData((prev) => ({
      ...prev,
      travellers: [...prev.travellers, { ...EMPTY_TRAVELLER }],
    }));
  };

  const removeTraveller = (idx) => {
    setFormData((prev) => ({
      ...prev,
      travellers: prev.travellers.filter((_, i) => i !== idx),
    }));
  };

  const updateTraveller = (idx, field, value) => {
    setFormData((prev) => {
      const travellers = [...prev.travellers];
      travellers[idx] = { ...travellers[idx], [field]: value };
      return { ...prev, travellers };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.destination_country) {
      toast.error("Please select a destination country");
      return;
    }
    if (!formData.visa_type) {
      toast.error("Please select a visa type");
      return;
    }
    if (!formData.nationality.trim()) {
      toast.error("Please enter your nationality");
      return;
    }
    if (formData.travellers.some((t) => !t.full_name.trim())) {
      toast.error("Please enter the full name for each traveller");
      return;
    }
    if (formData.travellers.some((t) => !t.nationality.trim())) {
      toast.error("Please enter the nationality for each traveller");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/visa-requests`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        destination_country: formData.destination_country,
        visa_type: formData.visa_type,
        nationality: formData.nationality,
        travel_date: formData.travel_date || null,
        travellers: formData.travellers,
        customer_notes: formData.customer_notes || null,
        consent_given: consentGiven,
        turnstile_token: turnstileToken,
      });
      setRequestNumber(response.data.request_number);
      setSubmitted(true);
      toast.success("Visa request submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setRequestNumber("");
    setTurnstileToken("");
    setConsentGiven(false);
    setFormData({
      first_name: "", last_name: "", email: "", phone: "",
      destination_country: "", visa_type: "",
      nationality: "", travel_date: "",
      travellers: [{ ...EMPTY_TRAVELLER }],
      customer_notes: "",
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 900 }}>
            Request Submitted!
          </h2>
          <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-200">
            <p className="text-sm text-gray-500 mb-2">Your Request Number</p>
            <p className="text-3xl font-mono font-bold text-gray-900 tracking-wider">{requestNumber}</p>
          </div>
          <p className="text-gray-600 mb-8">
            Thank you for your visa request. We've sent a confirmation to your email. We will get back to you within 24 hours with the next steps.
          </p>
          <Button onClick={resetForm} className="tbf-submit-btn rounded-md px-8 h-12">
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PublicLayout>
      {/* Page Title */}
      <div className="text-center pt-10 pb-6">
        <h1 className="tbf-page-title">VISA SERVICES</h1>
      </div>

      {/* Countries we cover */}
      <div style={{ background: "#f3e8ff", borderTop: "1px solid #e9d5ff", borderBottom: "1px solid #e9d5ff" }}>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <p style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "0.8rem", color: "#7c3aed", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "14px", textAlign: "center" }}>
            We apply visas for these countries
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
            {VISA_COUNTRIES.map((country) => {
              const selected = formData.destination_country === country;
              return (
                <button
                  key={country}
                  type="button"
                  onClick={() => handleInputChange("destination_country", country)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 12px",
                    borderRadius: "999px",
                    border: selected ? "2px solid #6C3084" : "1px solid #e9d5ff",
                    background: selected ? "#f3e8ff" : "white",
                    color: selected ? "#4a1d72" : "#4a1d72",
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "0.8rem",
                    fontWeight: selected ? 700 : 500,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <FlagImage country={country} />
                  {country}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="tbf-hero-heading mb-2">We apply.</h2>
            <h2 className="tbf-hero-heading-accent mb-6">You travel.</h2>
            <p className="text-gray-600 leading-relaxed mb-4" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1rem" }}>
              Skip the paperwork. Fill in the form below and we'll handle your entire visa application — document preparation, form completion, and submission on your behalf.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1rem" }}>
              We'll contact you within 24 hours to confirm the requirements for your specific nationality and destination.
            </p>
            <a href={WHATSAPP_GROUP} target="_blank" rel="noopener noreferrer" className="tbf-whatsapp-btn">
              Prefer WhatsApp? Join our group
            </a>
          </div>
          <div className="hidden lg:flex justify-center">
            <img src={VISA_IMAGE} alt="Visa illustration" className="w-full max-w-md" />
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="tbf-form-section">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <form onSubmit={handleSubmit} className="space-y-8">

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div>
                <label className="tbf-label">First Name <span className="text-red-500">*</span></label>
                <input type="text" value={formData.first_name} onChange={(e) => handleInputChange("first_name", e.target.value)} required placeholder="E.g. John" className="tbf-input" />
              </div>
              <div>
                <label className="tbf-label">Last Name <span className="text-red-500">*</span></label>
                <input type="text" value={formData.last_name} onChange={(e) => handleInputChange("last_name", e.target.value)} required placeholder="E.g. Doe" className="tbf-input" />
              </div>
              <div>
                <label className="tbf-label">Email <span className="text-red-500">*</span></label>
                <input type="email" value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} required placeholder="E.g. john@doe.com" className="tbf-input" />
              </div>
              <div>
                <label className="tbf-label">Phone <span className="text-red-500">*</span></label>
                <input type="text" value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} required placeholder="E.g. +31 6 1234567" className="tbf-input" />
              </div>
            </div>

            {/* Row 1: Destination + Visa Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="tbf-label">Destination Country <span className="text-red-500">*</span></label>
                <Select value={formData.destination_country} onValueChange={(v) => handleInputChange("destination_country", v)}>
                  <SelectTrigger className="tbf-select-trigger">
                    <SelectValue placeholder="Select country">
                      {formData.destination_country && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                          <FlagImage country={formData.destination_country} />
                          {formData.destination_country}
                        </span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {VISA_COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                          <FlagImage country={c} />
                          {c}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="tbf-label">Visa Type <span className="text-red-500">*</span></label>
                <Select value={formData.visa_type} onValueChange={(v) => handleInputChange("visa_type", v)}>
                  <SelectTrigger className="tbf-select-trigger">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {VISA_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Nationality + Travel Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="tbf-label">Your Nationality <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.nationality}
                  onChange={(e) => handleInputChange("nationality", e.target.value)}
                  placeholder="e.g. Dutch, British"
                  className="tbf-input"
                />
              </div>
              <div>
                <label className="tbf-label">Planned Travel Date</label>
                <input
                  type="date"
                  value={formData.travel_date}
                  onChange={(e) => handleInputChange("travel_date", e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="tbf-input"
                  style={{ colorScheme: "light" }}
                />
              </div>
            </div>

            {/* Travellers */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="tbf-label" style={{ marginBottom: 0 }}>
                  Travellers <span className="text-red-500">*</span>
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTraveller}
                  style={{ borderColor: "#6837B8", color: "#6837B8", fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.8rem" }}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add Traveller
                </Button>
              </div>

              <div className="space-y-4">
                {formData.travellers.map((traveller, idx) => (
                  <div
                    key={idx}
                    style={{
                      border: "1px solid #e9d5ff",
                      borderRadius: "10px",
                      padding: "20px",
                      background: "#faf5ff",
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "0.85rem", color: "#6C3084" }}>
                        Traveller {idx + 1}
                      </span>
                      {idx > 0 && (
                        <button
                          type="button"
                          onClick={() => removeTraveller(idx)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: "2px" }}
                          aria-label="Remove traveller"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="tbf-label">Full Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={traveller.full_name}
                          onChange={(e) => updateTraveller(idx, "full_name", e.target.value)}
                          placeholder="As on passport"
                          className="tbf-input"
                          style={{ background: "#fff" }}
                        />
                      </div>
                      <div>
                        <label className="tbf-label">Nationality <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={traveller.nationality}
                          onChange={(e) => updateTraveller(idx, "nationality", e.target.value)}
                          placeholder="e.g. Dutch"
                          className="tbf-input"
                          style={{ background: "#fff" }}
                        />
                      </div>
                      <div>
                        <label className="tbf-label">Date of Birth</label>
                        <input
                          type="date"
                          value={traveller.date_of_birth}
                          onChange={(e) => updateTraveller(idx, "date_of_birth", e.target.value)}
                          className="tbf-input"
                          style={{ background: "#fff", colorScheme: "light" }}
                        />
                      </div>
                      <div>
                        <label className="tbf-label">Passport Number <span style={{ fontWeight: 400, color: "#999" }}>(optional)</span></label>
                        <input
                          type="text"
                          value={traveller.passport_number}
                          onChange={(e) => updateTraveller(idx, "passport_number", e.target.value)}
                          placeholder="e.g. AA1234567"
                          className="tbf-input"
                          style={{ background: "#fff" }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="tbf-label">Additional Notes</label>
              <textarea
                value={formData.customer_notes}
                onChange={(e) => handleInputChange("customer_notes", e.target.value)}
                placeholder="E.g. any previous visa refusals, urgency, multiple-entry requirement..."
                rows={3}
                className="tbf-input resize-none"
              />
            </div>

            {/* Turnstile */}
            <div>
              <div
                className="cf-turnstile"
                data-sitekey={TURNSTILE_SITE_KEY}
                ref={(el) => {
                  if (el && window.turnstile && !el.dataset.rendered) {
                    el.dataset.rendered = "true";
                    window.turnstile.render(el, {
                      sitekey: TURNSTILE_SITE_KEY,
                      callback: (token) => setTurnstileToken(token),
                    });
                  }
                }}
              />
            </div>

            {/* Consent */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={consentGiven}
                onCheckedChange={setConsentGiven}
                className="mt-0.5 border-gray-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
              />
              <label htmlFor="consent" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                By submitting this form, you agree to our{" "}
                <Link to="/terms" className="text-purple-600 hover:underline font-medium">Terms and Conditions</Link>
                {" "}and{" "}
                <Link to="/privacy" className="text-purple-600 hover:underline font-medium">Privacy Policy</Link>.
              </label>
            </div>

            <Button
              type="submit"
              className="tbf-submit-btn w-full h-14 text-base font-semibold"
              disabled={loading || !consentGiven || !turnstileToken}
            >
              {loading ? "Submitting..." : "Submit Visa Request"}
            </Button>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
}
